import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowClockwise, Eye, X } from "@phosphor-icons/react";

const ENTITY_LABELS = {
  sku: "SKU",
  material: "Material",
  batch: "Batch",
  qc: "QC",
  fg: "Finished Goods",
  sales_order: "Sales Order",
};

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-blue-100 text-blue-800",
  soft_delete: "bg-red-100 text-red-800",
  hard_delete: "bg-red-100 text-red-800",
  mark_inactive: "bg-orange-100 text-orange-800",
  archive: "bg-orange-100 text-orange-800",
  restore: "bg-emerald-100 text-emerald-800",
  cancel: "bg-red-100 text-red-800",
  duplicate: "bg-purple-100 text-purple-800",
  stock_adjust: "bg-amber-100 text-amber-800",
  adjust: "bg-amber-100 text-amber-800",
  advance: "bg-blue-100 text-blue-800",
  update_sop: "bg-blue-100 text-blue-800",
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { hour12: false });
}

function ChangesDialog({ open, row, onClose }) {
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit Detail</DialogTitle>
          <DialogDescription className="sr-only">Audit change payload</DialogDescription>
        </DialogHeader>
        {!row ? null : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label className="text-xs text-muted-foreground">Entity</Label><div>{ENTITY_LABELS[row.entity_type] || row.entity_type} · <span className="mono">{row.entity_ref}</span></div></div>
              <div><Label className="text-xs text-muted-foreground">Reference</Label><div>{row.entity_label || "—"}</div></div>
              <div><Label className="text-xs text-muted-foreground">Action</Label><div><Badge className={ACTION_COLORS[row.action] || "bg-muted"}>{row.action}</Badge></div></div>
              <div><Label className="text-xs text-muted-foreground">User</Label><div>{row.user}{row.role ? ` (${row.role})` : ""}</div></div>
              <div className="col-span-2"><Label className="text-xs text-muted-foreground">Timestamp</Label><div className="mono">{fmtTime(row.at)}</div></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Change Payload</Label>
              <pre className="mt-1 p-3 rounded-md bg-muted/40 border text-[11px] mono max-h-[400px] overflow-auto whitespace-pre-wrap">
{JSON.stringify(row.changes || {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AuditLog() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Filters
  const [entityType, setEntityType] = useState("");
  const [user, setUser] = useState("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const params = () => {
    const p = { limit, offset };
    if (entityType) p.entity_type = entityType;
    if (user) p.user = user;
    if (action) p.action = action;
    if (q) p.q = q;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: feed }, { data: sum }] = await Promise.all([
        api.get("/audit-log", { params: params() }),
        summary ? Promise.resolve({ data: summary }) : api.get("/audit-log/summary"),
      ]);
      setRows(feed.rows);
      setTotal(feed.total);
      setUsers(feed.users);
      setActions(feed.actions);
      setEntityTypes(feed.entity_types);
      if (!summary) setSummary(sum);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, user, action, fromDate, toDate, offset]);
  // Debounced free-text search
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const clear = () => {
    setEntityType(""); setUser(""); setAction(""); setQ(""); setFromDate(""); setToDate(""); setOffset(0);
  };

  const hasFilters = entityType || user || action || q || fromDate || toDate;

  return (
    <div data-testid="audit-log-page">
      <PageHeader subtitle="Governance" title="Audit Log" />

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
          <Card className="shadow-none">
            <CardContent className="p-4">
              <div className="overline mb-1 text-[10px]">30-day events</div>
              <div className="text-metric text-2xl">{summary.total_30d.toLocaleString()}</div>
            </CardContent>
          </Card>
          {Object.entries(summary.by_entity).map(([k, v]) => (
            <Card key={k} className="shadow-none cursor-pointer hover:border-primary transition-colors"
                   onClick={() => { setOffset(0); setEntityType(k); }} data-testid={`audit-filter-entity-${k}`}>
              <CardContent className="p-4">
                <div className="overline mb-1 text-[10px]">{ENTITY_LABELS[k]}</div>
                <div className="text-metric text-2xl">{v}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="shadow-none mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2 relative">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
                <Input className="pl-8" placeholder="Batch #, SKU code, user, action…"
                        value={q} onChange={e => setQ(e.target.value)} data-testid="audit-search"/>
              </div>
            </div>
            <div>
              <Label className="text-xs">Entity</Label>
              <Select value={entityType || "__all__"} onValueChange={v => { setEntityType(v === "__all__" ? "" : v); setOffset(0); }}>
                <SelectTrigger data-testid="audit-entity-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All entities</SelectItem>
                  {entityTypes.map(t => <SelectItem key={t} value={t}>{ENTITY_LABELS[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">User</Label>
              <Select value={user || "__all__"} onValueChange={v => { setUser(v === "__all__" ? "" : v); setOffset(0); }}>
                <SelectTrigger data-testid="audit-user-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All users</SelectItem>
                  {users.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={action || "__all__"} onValueChange={v => { setAction(v === "__all__" ? "" : v); setOffset(0); }}>
                <SelectTrigger data-testid="audit-action-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All actions</SelectItem>
                  {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">From</Label><Input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setOffset(0); }} data-testid="audit-from-date"/></div>
              <div><Label className="text-xs">To</Label><Input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setOffset(0); }} data-testid="audit-to-date"/></div>
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3 flex justify-end">
              <Button size="sm" variant="ghost" onClick={clear} data-testid="audit-clear-filters">
                <X size={14} className="mr-1"/>Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">No audit events match your filters.</TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="mono text-xs text-muted-foreground whitespace-nowrap">{fmtTime(r.at)}</TableCell>
                  <TableCell><Badge variant="secondary" className="uppercase text-[9px]">{ENTITY_LABELS[r.entity_type] || r.entity_type}</Badge></TableCell>
                  <TableCell>
                    <div className="mono text-sm font-semibold">{r.entity_ref || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[220px]">{r.entity_label || ""}</div>
                  </TableCell>
                  <TableCell><Badge className={`${ACTION_COLORS[r.action] || "bg-muted"} hover:${ACTION_COLORS[r.action] || "bg-muted"}`}>{r.action}</Badge></TableCell>
                  <TableCell className="text-sm">{r.user || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.role || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setSelected(r)} data-testid={`audit-view-${r.id}`}>
                      <Eye size={14}/>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          {loading ? "Loading…" : `${Math.min(offset + 1, total)}–${Math.min(offset + limit, total)} of ${total.toLocaleString()}`}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="audit-prev">
            Previous
          </Button>
          <Button size="sm" variant="outline" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="audit-next">
            Next
          </Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="audit-refresh">
            <ArrowClockwise size={14} className="mr-1"/>Refresh
          </Button>
        </div>
      </div>

      <ChangesDialog open={!!selected} row={selected} onClose={() => setSelected(null)}/>
    </div>
  );
}
